import nacl from 'tweetnacl';
import { sha512 } from '@noble/hashes/sha512';

/**
 * Performs one layer of domain-separated authenticated encryption using XSalsa20-Poly1305.
 *
 * This function derives a unique encryption key from a shared secret and a numeric layer identifier,
 * enforcing strict key separation across multiple encryption layers.
 *
 * Key derivation:
 *   - derivedKey = SHA-512(sharedSecret || "unlink-encryption-layer<N>")
 *   - first 32 bytes of the hash are used as the XSalsa20 key
 *
 * Encryption:
 *   - XSalsa20-Poly1305 via NaCl's secretbox (32-byte key, 24-byte nonce, 16-byte MAC tag)
 *
 * @param nonce        A 24-byte nonce (must be unique per message/key).
 * @param message      The input to encrypt — either padded plaintext or ciphertext from a previous layer.
 * @param sharedSecret A 32-byte shared secret (e.g., from ECDH or hybrid secret material).
 * @param layer        Numeric layer identifier. Used to isolate encryption keys across layers.
 *
 * @returns An object containing:
 *   - ciphertext: The authenticated ciphertext (includes Poly1305 tag).
 */
export function encryptLayer(
  nonce: Uint8Array,
  message: Uint8Array,
  sharedSecret: Uint8Array,
  layer: number
): { ciphertext: Uint8Array } {
  // === Input validation ===
  if (nonce.length !== nacl.secretbox.nonceLength) {
    throw new Error(`Nonce must be ${nacl.secretbox.nonceLength} bytes`);
  }
  if (sharedSecret.length < 32) {
    throw new Error('sharedSecret must be at least 32 bytes');
  }

  // === Build domain-separated label based on the numeric layer ===
  // Prevents key reuse across layered encryptions (e.g., "unlink-encryption-layer1")
  const label = new TextEncoder().encode(`unlink-encryption-layer${layer}`);

  // === Prepare input for key derivation: sharedSecret || label ===
  const input = new Uint8Array(sharedSecret.length + label.length);
  input.set(sharedSecret);
  input.set(label, sharedSecret.length);

  // === Derive encryption key using SHA-512, then truncate to 32 bytes ===
  const derivedKey = sha512(input).slice(0, 32); // XSalsa20 requires 256-bit key

  // === Encrypt and authenticate the message using secretbox ===
  const ciphertext = nacl.secretbox(message, nonce, derivedKey);

  return { ciphertext };
}